import { Flex } from '@chakra-ui/react';
import { SpinnerLoader, Text } from '@kula-ai/components';
import { ViewFile } from '@/components/ats/FileUpload/ViewFile';
import { formatBytes } from '@/utils/format-bytes';
import { SCORECARD_GENERATING_ROW_LABEL } from './attachFilesCopy';
import type { SelectedAttachment } from './useOfferAttachments';

interface SelectedAttachmentChipsProps {
  attachments: SelectedAttachment[];
  // Deselect ONLY — never a delete/purge call (contract seam_2 MUST_NOT, case X1).
  onRemove: (attachment: SelectedAttachment) => void;
}

// Design.md `2498:517` — chips under the note body: filename, size, preview, ✕.
export const SelectedAttachmentChips = ({
  attachments,
  onRemove,
}: SelectedAttachmentChipsProps) => {
  if (!attachments.length) return null;

  return (
    <Flex
      direction="column"
      gap="8px"
      p="12px 16px"
      data-test-id="selected-attachment-chips"
    >
      {attachments.map((attachment) =>
        // The PDF starts rendering when Add is clicked, so this is the chip the
        // recruiter is looking at when it happens. Preview and ✕ would both act on
        // a blob that doesn't exist yet, so the loader takes their place.
        attachment.status === 'generating' ? (
          <Flex
            key={`${attachment.ref_type}:${attachment.ref_id ?? ''}`}
            align="center"
            gap="8px"
            data-test-id="attachment-chip-generating"
          >
            <SpinnerLoader size="sm" data-test-id="attachment-chip-spinner" />
            <Text variant="span" color="muted">
              {attachment.filename} — {SCORECARD_GENERATING_ROW_LABEL}
            </Text>
          </Flex>
        ) : (
          <ViewFile
            key={`${attachment.ref_type}:${attachment.ref_id ?? ''}`}
            file={{
              filename: attachment.filename,
              content_type: attachment.content_type,
              // AMENDMENT 1 (contract.yml) — always present, a plain
              // signed blob URL for every ref_type (amendment_6 made
              // scorecards a normal pre-generated blob too). The client
              // never needed to special-case it either way.
              url: attachment.url,
            }}
            // byte_size is required on every SelectedAttachment (contract.yml
            // seam_1 + amendment_6_pregenerate_scorecard_pdf — always a real
            // integer, never null/undefined) — no nil-render guard needed.
            subtitle={formatBytes(attachment.byte_size)}
            showIcon
            showPreview
            showDownload={false}
            showDelete
            // "Remove", not "Delete": the ✕ deselects the reference and never
            // purges the underlying blob (contract.yml seam_2 MUST_NOT).
            deleteLabel="Remove"
            handleDelete={() => onRemove(attachment)}
            hideTextReference={false}
          />
        )
      )}
    </Flex>
  );
};
